import { useEffect, useState } from "react";
import type { TvRoomOfferFeed } from "@shared/types/roomOffer";
import { getBinding } from "../lib/binding";
import { fetchBounded } from "../lib/boundedFetch";

/**
 * What this hotel is selling right now, for the television (`POS-3`).
 *
 * A second request rather than a block of the premise config, because the two
 * change on completely different clocks: a config is edited by an operator and
 * pushed over the realtime channel, while an offer goes live and dark on its own
 * schedule (`OfferAvailability`) with nobody touching anything. Folding offers
 * into the config would mean either a stale shop or a config refetch every few
 * minutes for every premise, offers or not.
 *
 * The server resolves availability against `now` and sends only what is live
 * (POS-2), so this hook holds no rules of its own and simply re-asks.
 */

/**
 * How often to re-ask.
 *
 * Stated plainly, because it is a real limit rather than an exact answer: an
 * `OfferAvailability` window is authored to the MINUTE, so an offer can sit on
 * the rail for up to five minutes after its window closed, and a window shorter
 * than five minutes can be missed entirely. That is accepted — the alternative
 * is a per-minute request from every television in the property for a shop that
 * changes a few times a day, and the server refuses a stale request anyway, so
 * the cost of the lag is a guest seeing an offer they cannot have rather than
 * one they can take.
 *
 * If short windows ever become a real authoring pattern, the fix is a realtime
 * `offers_changed` push on the existing hub (as `config_changed` already does),
 * not a faster timer.
 */
export const OFFERS_POLL_MS = 5 * 60_000;

/** Backoff for an API that is not answering. Same shape as `useConfig`'s. */
const RETRY_BASE_MS = 5_000;
const RETRY_MAX_MS = 60_000;

/**
 * The offers feed for a premise, or `undefined` until one has arrived.
 *
 * `undefined` is load-bearing: `screenContentCount` reads it as "nothing to
 * show", which keeps the Offers tile off the home screen until the answer is in.
 * A tile that appeared and then disappeared would be worse than one that
 * appeared a beat late.
 *
 * A failed request keeps the LAST GOOD feed rather than blanking the shop - the
 * same instinct `useConfig` has about the cached config. A hotel Wi-Fi blip must
 * not empty a guest's menu mid-browse.
 *
 * `preview` short-circuits the whole thing: the design preview
 * (`?scheme=<key>&on=offers`) has no premise and no backend, so it is handed a
 * fixture instead. A bound television never receives one.
 */
export function useRoomOffers(
  premiseId: string | undefined,
  preview?: TvRoomOfferFeed
): TvRoomOfferFeed | undefined {
  const [feed, setFeed] = useState<TvRoomOfferFeed | undefined>(undefined);

  useEffect(() => {
    // Whose shop this is has changed, so what we are holding is somebody else's.
    // The CMS live preview keeps one iframe mounted across a premise switch, and
    // switching the screen off and on again passes `undefined` and back: in both
    // cases the previous hotel's offers would otherwise keep rendering as if
    // they were current.
    setFeed(undefined);
    if (preview || !premiseId) return;
    let alive = true;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (ms: number) => {
      timer = setTimeout(() => {
        timer = undefined;
        void load();
      }, ms);
    };

    async function load(): Promise<void> {
      try {
        // Premise fetches carry the screen-token from pairing. A CMS preview tab
        // has none and is recognised by its session cookie instead, which is
        // what lets the template editor's live preview show the real shop.
        const headers: Record<string, string> = {};
        const token = getBinding()?.screenToken;
        if (token) headers["X-Screen-Token"] = token;
        // Bounded: see `fetchBounded` — an unanswered request kills the poll
        // loop outright rather than failing it.
        const res = await fetchBounded(
          `/api/frontlayer/v1/premise/${premiseId}/offers`,
          { headers }
        );
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const next = (await res.json()) as TvRoomOfferFeed;
        if (!alive) return;
        failures = 0;
        // A body without the array is a proxy or a captive portal answering for
        // the API, not a shop with nothing in it - keep the last good feed.
        if (Array.isArray(next?.offers)) setFeed(next);
        schedule(OFFERS_POLL_MS);
      } catch {
        if (!alive) return;
        failures += 1;
        schedule(Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (failures - 1)));
      }
    }

    void load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [premiseId, preview]);

  return preview ?? feed;
}
